import { DataStorage } from '@studyportals/data-storage';

import state from "./state";
import mutations from "./mutations";

import { IResponseBody } from "@/interfaces/external-services.interface";
import { IActions } from "@/interfaces/state.interface";
import { PortalType } from "@/enums/portal-types";
import { IQuestion } from "@/interfaces/questions.interface";

import { PersonalityTestResults } from "@/classes/personality-test-results";
import { PersonalityTestTracker } from "@/classes/tracker";
import questions from '@/../static/data/questions.json';
const ANSWERS_STORAGE_LABEL = "PersonalityTestAnswers";

declare const TEST_RESULT_API_URL: string;

const actions: IActions = {
	answerQuestion: (answerObject) => {
		mutations.answerQuestion(answerObject);
		actions.copyAnswersToLocalStorage(state.questions);
	},
	setPreferredPortal: (preferredPortalType) => {
		mutations.setPreferredPortal(preferredPortalType);
	},
	setOriginPortal: (): void => {
		const portal = window.location.host;
		let portalType = PortalType.MASTERS;

		switch (true) {
			case (portal.indexOf('mastersportal.com') !== -1):
				portalType = PortalType.MASTERS;
				break;
			case (portal.indexOf('bachelorsportal.com') !== -1):
				portalType = PortalType.BACHELORS;
				break;
			case (portal.indexOf('phdportal.com') !== -1):
				portalType = PortalType.PHD;
				break;
			case (portal.indexOf('distancelearningportal.com') !== -1):
				portalType = PortalType.DISTANCE_LEARNING;
				break;
			case (portal.indexOf('shortcoursesportal.com') !== -1):
				portalType = PortalType.SHORT_COURSES;
				break;
			case (portal.indexOf('preparationcoursesportal.com') !== -1):
				portalType = PortalType.PREPARATION_COURSES;
				break;
		}

		mutations.setOriginPortal(portalType);
	},

	copyAnswersToLocalStorage: (answers) => {
		// Store the answers in local storage for one day.
		DataStorage.store(ANSWERS_STORAGE_LABEL, JSON.stringify(answers), 60 * 60 * 24);
	},

	storeResults: async () => {
		const sessionService = window.SessionService;
		if (!sessionService) {
			return;
		}

		const session = await sessionService.getSession();
		if (!session) {
			return;
		}

		const data = {
			identityId: session.getUser().identityId,
			testType: "PersonalityTest",
			answers: state.questions.map(
				question => ({
					question: question.text,
					answer: question.answer.toString()
				})
			),
			result: state.results.disciplines.map(
				discipline => discipline.id
			),
			code: state.results.threeLetterCode
		};

		try {
			const response = await fetch(`${TEST_RESULT_API_URL}results`, {
				method: 'POST',
				body: JSON.stringify(data)
			});
			const responseBody = await response.json() as IResponseBody;

			if (!response.ok || responseBody.failed) {
				throw new Error("Storing results failed.");
			}
		} catch (error) {
			// eslint-disable-next-line @typescript-eslint/restrict-template-expressions
			console.error(`Storing results failed: ${error}`);
		}
	},

	getResults: () => {
		state.results = PersonalityTestResults.getResults(state.questions);

		// Track the three letter code.
		PersonalityTestTracker.fireTracking(
			'results_three_letter_code',
			state.results.threeLetterCode
		);

		// Track each personality type match separately.
		state.results.personalityTypes.forEach(type =>
			PersonalityTestTracker.fireTracking(
				`results_personality_match_${type.trait.toLowerCase()}`,
				type.score.toString()
			)
		);

		const disciplineIds = state.results.disciplines.map(discipline => discipline.id);

		// Track the four suggested disciplines.
		PersonalityTestTracker.fireTracking(
			'results_disciplines',
			JSON.stringify(disciplineIds)
		);

		void actions.saveDisciplinesToProfile(disciplineIds);
		void actions.storeResults();
	},

	saveDisciplinesToProfile: async (disciplines) => {
		const student = window.Student;
		if (!state.isLoggedIn || !student || disciplines.length === 0 || typeof disciplines.length === 'undefined') {
			return;
		}

		try {
			await student.addInterests('discipline', disciplines);
		} catch (error) {
			// eslint-disable-next-line @typescript-eslint/restrict-template-expressions
			console.warn(`PersonalityTest - saving of disciplines failed - ${error}`);
		}
	},

	retrieveStoredAnswers: () => {
		const answers = DataStorage.retrieve(ANSWERS_STORAGE_LABEL) as string | null;
		if (!answers || !answers.length) {
			return;
		}

		state.questions = JSON.parse(answers) as IQuestion[];
	},

	resetStoredAnswers: () => {
		const blankAnswers = JSON.parse(JSON.stringify(questions.config)) as IQuestion[];
		state.questions = blankAnswers;
		actions.copyAnswersToLocalStorage(blankAnswers);
	}
};

export default actions;